import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { Box, Divider, Skeleton, Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import {
    GroupedTileConfig,
    GroupedTileContent,
    GroupedTileData,
    GroupedTileHeaderConfig,
} from "src/components/cards/grouped-tile/dataTypes";
import { useSeverityColor } from "src/hooks/useSeverityColor";
import { useHighlightedFont } from "src/hooks/useHighlightedFont";
import { useScreenSize } from "src/hooks/useScreenSize";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";
import { SenecCard } from "src/components/cards/base/SenecCard";
import { ReactElement } from "react";

export interface GroupTileProps {
    tileConfig?: GroupedTileConfig;
    isLoading: boolean;
    highlightLeft?: boolean;
    flatView?: boolean;
    onClick?: () => void;
    sx?: SxProps<Theme>;
    footer?: ReactElement;
}

export interface GroupedTileContentProps {
    tileConfig: GroupedTileConfig;
    highlightLeft?: boolean;
    flatView?: boolean;
}

/**
 * @deprecated Replaced by GroupedCardContent component.
 */
export const GroupedTile = ({
    tileConfig,
    isLoading,
    highlightLeft = false,
    flatView = false,
    onClick,
    sx,
    footer,
}: GroupTileProps) => {
    const { isMobile } = useScreenSize();
    const handleTileClick = () => {
        if (!onClick) return;
        onClick();
    };

    return isLoading || !tileConfig ? (
        <Skeleton data-testid="skeleton" variant="rounded" sx={{ borderRadius: "0.75rem" }} width="100%" height="200px" />
    ) : (
        <SenecCard
            onClick={() => handleTileClick()}
            data-testid="grouped-tile"
            sx={{ height: "100%", minWidth: "220px", "&:hover": { cursor: onClick && "pointer" }, ...sx }}
        >
            <Stack spacing={isMobile ? 3 : 4}>
                <TileHeader title={tileConfig.tileHeader?.title} statusIcon={tileConfig.tileHeader?.statusIcon} />
                <GroupedTileBodyContent tileConfig={tileConfig} highlightLeft={highlightLeft} flatView={flatView} />
                {footer}
            </Stack>
        </SenecCard>
    );
};

export const GroupedTileBodyContent = ({ tileConfig, highlightLeft = false, flatView = false }: GroupedTileContentProps) => {
    const { isMobile } = useScreenSize();

    return (
        <Stack data-testid={"tile-body"}>
            {isMobile ? (
                <MobileTileBody groupedData={tileConfig.groupedData} highlightLeft={highlightLeft} />
            ) : flatView ? (
                <FlatTileBody groupedData={tileConfig.groupedData} highlightLeft={highlightLeft} />
            ) : (
                <DesktopTileBody groupedData={tileConfig.groupedData} highlightLeft={highlightLeft} />
            )}
        </Stack>
    );
};

export const TileHeader = ({ title, statusIcon }: GroupedTileHeaderConfig) => {
    if (!title && !statusIcon) return <></>;

    return (
        <Stack direction={"row"} justifyContent={"space-between"} data-testid={"tile-header"}>
            <Typography variant="h4" sx={{ alignSelf: "center" }}>
                {title}
            </Typography>
            <Stack direction={"row"} alignItems={"center"}>
                {statusIcon && (
                    <Stack direction={"row"} alignItems={"center"} pl={"0.5rem"}>
                        {statusIcon}
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export interface TileBodyProps {
    groupedData: Record<string, GroupedTileData[]>;
    highlightLeft: boolean;
}

const tableCellStyle = {
    border: 0,
    p: 0,
};

export const MobileTileBody = ({ groupedData, highlightLeft }: TileBodyProps) => {
    return (
        <Stack spacing={3}>
            {Object.values(groupedData).map((group: GroupedTileData[], index) => (
                <Stack key={"tile-group-" + index}>
                    <Stack spacing={3}>
                        {group.map((tileData: GroupedTileData) => (
                            <MobileTileRow key={tileData.label} tileData={tileData} highlightLeft={highlightLeft} />
                        ))}
                        {Object.entries(groupedData).length > index + 1 && (
                            <Stack alignContent={"center"} key={index}>
                                <Divider data-testid={"group-divider"} sx={{ borderColor: "neutralGray30.main" }} />
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );
};

export const MobileTileRow = ({ tileData, highlightLeft }: { tileData: GroupedTileData; highlightLeft: boolean }) => {
    const { getHighlightedFont } = useHighlightedFont();
    const { getColor } = useSeverityColor();
    const theme: SenecFPPTheme = useTheme();

    return (
        // Row
        <Stack
            key={"tile-row-" + tileData.label}
            data-testid={"tile-row"}
            style={{ color: getColor(tileData.severity) }}
            sx={{
                position: "relative",
                width: "100%",
                pr: tileData.content?.endAdornment ? "2rem" : "0",
            }}
        >
            {/* Label */}
            <Typography variant={getHighlightedFont(highlightLeft)} sx={{ pb: "4px", color: theme.palette.neutralGray70.main }}>
                {tileData.label}
            </Typography>
            {/* Value */}
            <TileContentValue highlightLeft={highlightLeft} content={tileData.content} />
            <Stack sx={{ position: "absolute", right: 0, top: "calc(calc(100% - 1.5rem) * 0.5)" }}>
                {tileData.content?.endAdornment}
            </Stack>
        </Stack>
    );
};

export const FlatTileBody = ({ groupedData, highlightLeft }: TileBodyProps) => {
    const { getHighlightedFont } = useHighlightedFont();
    const { getColor } = useSeverityColor();
    const theme: SenecFPPTheme = useTheme();

    return (
        <Stack direction={"row"} justifyContent={"flex-start"} spacing={3} flexWrap="wrap" useFlexGap>
            {Object.values(groupedData).map((group: GroupedTileData[]) =>
                group.map((tileData: GroupedTileData) => (
                    // Item
                    <Stack key={"tile-row-" + tileData.label} data-testid={"tile-row"}>
                        {/* Label */}
                        <Typography
                            variant={getHighlightedFont(highlightLeft)}
                            sx={{ pb: "4px", color: theme.palette.neutralGray70.main }}
                        >
                            {tileData.label}
                        </Typography>
                        {/* Value */}
                        <Stack
                            style={{ color: getColor(tileData.severity) }}
                            display={"flex"}
                            direction={"row"}
                            justifyContent={"left"}
                            alignItems={"center"}
                        >
                            <TileContentValue highlightLeft={highlightLeft} content={tileData.content} flatView={true} />
                            {tileData.content?.endAdornment}
                        </Stack>
                    </Stack>
                )),
            )}
        </Stack>
    );
};

export const DesktopTileBody = ({ groupedData, highlightLeft }: TileBodyProps) => {
    return (
        <TableContainer>
            <Table aria-label="simple-table" size="small">
                {Object.values(groupedData).map((group: GroupedTileData[], index) => (
                    <TableBody
                        key={"tile-group-" + index}
                        sx={{
                            "& .MuiTableCell-root": {
                                pt: "1rem",
                            },
                            "& .MuiTableRow-root:first-of-type > .MuiTableCell-root": {
                                pt: "0",
                            },
                        }}
                    >
                        {group.map((tileData: GroupedTileData, idx) => (
                            <TileTableRow key={idx} tileData={tileData} highlightLeft={highlightLeft} />
                        ))}
                        {/* Divider */}
                        {Object.entries(groupedData).length > index + 1 && <TableGroupDivider key={index} />}
                    </TableBody>
                ))}
            </Table>
        </TableContainer>
    );
};

export const TileTableRow = ({ tileData, highlightLeft }: { tileData: GroupedTileData; highlightLeft: boolean }) => {
    const { getHighlightedFont } = useHighlightedFont();
    const { getColor } = useSeverityColor();
    const theme: SenecFPPTheme = useTheme();

    return (
        <TableRow key={"tile-row-" + tileData.label} sx={{ border: 0, height: "100%" }}>
            {/* Label */}
            <TableCell
                sx={{
                    ...tableCellStyle,
                    width: "25%",
                    minWidth: "150px",
                    color: tileData.severity ? getColor(tileData.severity) : theme.palette.neutralGray70.main,
                }}
            >
                <Typography variant={getHighlightedFont(highlightLeft)}>{tileData.label}</Typography>
            </TableCell>
            {/* Content */}
            <TableCell
                sx={{
                    ...tableCellStyle,
                    pl: "1rem",
                    width: "75%",
                    verticalAlign: "baseline",
                    wordBreak: "break-word",
                    position: "relative",
                    pr: tileData.content?.endAdornment ? "2rem" : "0",
                }}
                style={{ color: getColor(tileData.severity) }}
            >
                <TileContentValue highlightLeft={highlightLeft} content={tileData.content} />
                <Stack sx={{ position: "absolute", right: 0, top: "1rem" }}>{tileData.content?.endAdornment}</Stack>
            </TableCell>
        </TableRow>
    );
};

const TableGroupDivider = () => {
    return (
        <TableRow sx={{ border: 0 }}>
            <TableCell
                colSpan={2}
                sx={{
                    "&.MuiTableCell-root": {
                        pt: "1.5rem",
                    },
                    ...tableCellStyle,
                    width: "100%",
                    height: "1.5rem",
                    pb: "1.5rem",
                }}
            >
                <Divider data-testid={"group-divider"} />
            </TableCell>
        </TableRow>
    );
};

export interface ContentElementProps {
    content?: GroupedTileContent;
    highlightLeft: boolean;
    flatView?: boolean;
}

export const TileContentValue = ({ content, highlightLeft, flatView = false }: ContentElementProps) => {
    const { getHighlightedFont } = useHighlightedFont();

    if (content === undefined) return <></>;

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                pr: flatView ? "0.5rem" : content.endAdornment ? "2rem" : "0",
            }}
            style={{
                display: "-webkit-box",
                overflow: "hidden",
                wordWrap: "break-word",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
            }}
        >
            <Stack>
                {typeof content.value === "string" ? (
                    <Typography variant={getHighlightedFont(!highlightLeft)}>{content.value}</Typography>
                ) : (
                    content.value
                )}
            </Stack>
        </Box>
    );
};
